import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";

const HEADER_GRADIENT = "linear-gradient(90deg, #0f2027, #203a43, #2c5364)";

/* Parse "dd/mm/yyyy" so announcements can be sorted newest first */
const parseDate = (str) => {
  if (!str) return 0;
  const [d, m, y] = str.split("/").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime() || 0;
};

/* Strip scripts / inline handlers from the HTML message before rendering */
const sanitizeHtml = (html = "") => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, iframe, object, embed").forEach((el) =>
    el.remove()
  );
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith("on") || value.startsWith("javascript:")) {
        el.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
};

/* Plain-text version of the HTML message (used to detect empty messages) */
const stripHtml = (html = "") =>
  new DOMParser().parseFromString(html, "text/html").body.textContent.trim();

const StudentAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  // 🔑 values from sessionStorage
  const token = sessionStorage.getItem("token");

  const handleViewMessage = (item) => {
    setSelectedAnnouncement(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAnnouncement(null);
  };

  /* ================= FETCH ANNOUNCEMENTS ================= */
  useEffect(() => {
    const fetchAnnouncements = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/Anouncement/Student_Anouncement`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          }
        );

        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

        const data = await res.json();
        console.log("✅ Announcements API:", data);

        const list = Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data)
          ? data
          : [];

        // newest first
        const sorted = [...list].sort(
          (a, b) => parseDate(b.createdOn) - parseDate(a.createdOn)
        );
        setAnnouncements(sorted);
      } catch (err) {
        console.error("❌ Announcements error:", err);
        setError("Could not load announcements. Please try again.");
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [token]);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          background: HEADER_GRADIENT,
          color: "#fff",
          p: 2.5,
          borderRadius: "8px",
          mb: 1,
          boxShadow: 3,
        }}
      >
        <Typography fontSize="26px" fontWeight="bold">
          Announcements
        </Typography>
      </Box>

      {/* ================= MAIN CARD ================= */}
      <Paper sx={{ p: 4, borderRadius: "12px" }}>
        {/* LOADER */}
        {loading && (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* ERROR */}
        {!loading && error && (
          <Typography align="center" color="error" py={4}>
            {error}
          </Typography>
        )}

        {/* ================= ANNOUNCEMENTS TABLE ================= */}
        {!loading && !error && announcements.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f1f3f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Posted On</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Posted By</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {announcements.map((a, index) => {
                  const hasMessage = stripHtml(a.message) !== "";

                  return (
                    <TableRow key={a.noticeBoardId ?? index} hover>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell sx={{ fontWeight: 500 }}>
                        {a.title || "—"}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            a.className
                              ? `${a.className}${
                                  a.sectiionName ? ` - ${a.sectiionName}` : ""
                                }`
                              : "—"
                          }
                          size="small"
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "rgba(44, 83, 100, 0.1)",
                            color: "#2c5364",
                          }}
                        />
                      </TableCell>

                      <TableCell>{a.createdOn || "—"}</TableCell>

                      <TableCell>{a.createdBy || "—"}</TableCell>

                      <TableCell>
                        {hasMessage ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewMessage(a)}
                            sx={{
                              textTransform: "none",
                              borderColor: "#2c5364",
                              color: "#2c5364",
                              "&:hover": {
                                borderColor: "#0f2027",
                                bgcolor: "rgba(44, 83, 100, 0.08)",
                              },
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* NO ANNOUNCEMENTS */}
        {!loading && !error && announcements.length === 0 && (
          <Typography align="center" color="text.secondary" py={4}>
            No announcements have been posted yet.
          </Typography>
        )}
      </Paper>

      {/* ================= MESSAGE MODAL ================= */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            background: HEADER_GRADIENT,
            color: "white",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Announcement
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Title:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f2027" }}>
              {selectedAnnouncement?.title || "—"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Posted On:
              </Typography>
              <Typography variant="body2">
                {selectedAnnouncement?.createdOn || "—"}
              </Typography>
            </Box>
            {selectedAnnouncement?.createdBy && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Posted By:
                </Typography>
                <Typography variant="body2">{selectedAnnouncement.createdBy}</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Message:
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f9f9f9",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                lineHeight: 1.6,
                wordBreak: "break-word",
                "& p": { m: 0, mb: 1 },
                "& p:last-child": { mb: 0 },
                "& img": { maxWidth: "100%" },
              }}
            >
              {selectedAnnouncement && stripHtml(selectedAnnouncement.message) ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(selectedAnnouncement.message),
                  }}
                />
              ) : (
                <Typography variant="body1">No message available</Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              background: HEADER_GRADIENT,
              color: "white",
              textTransform: "none",
              px: 3,
              "&:hover": {
                background: "linear-gradient(90deg, #2c5364, #203a43, #0f2027)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentAnnouncements;